import { getSettings, putFile, setSetting } from '../common/library/database';
import type { CraftId, CraftMessage, FileRole } from '../common/library/types';
import { installIndexedFileSystem } from './filesystem';
import { trackToolEvent, type ToolCategory } from '../common/analytics/toolEvents';

type NativeApi = {
  execute: (command: string, params?: Record<string, unknown>) => Promise<unknown>;
  request: (method: string, params?: Record<string, unknown>) => Promise<unknown>;
  importFiles?: (files: File[]) => Promise<unknown>;
  openProject?: (file: File) => Promise<unknown>;
  addFile?: (file: File) => Promise<unknown>;
  saveToBrowser?: (path?: string) => Promise<{ path: string }>;
  readFile?: (path: string) => Promise<Uint8Array> | Uint8Array;
  flush?: () => Promise<unknown>;
};
type CraftWindow = Window & {
  __editallCraftReady?: Promise<void>;
  editallBridge?: { appId: CraftId; storage: string; importFiles: (files: File[]) => Promise<void>; save: () => Promise<void>; flush: () => Promise<void>; request: (method: string, params?: Record<string, unknown>) => Promise<unknown> };
  filmcraft?: NativeApi;
  effectcraft?: NativeApi;
  lightcraft?: { command: (command: string, params: string) => Promise<string> };
  wasmBindings?: { editallRequest?: (method: string, params: string) => Promise<string> };
  filmcraftLoad?: { error?: string; readyMs?: number };
  effectcraftLoad?: { error?: string; readyMs?: number };
};
const host = window as CraftWindow;
const appId = (document.currentScript as HTMLScriptElement | null)?.dataset.craft as CraftId;
const pending = new Set<Promise<unknown>>();
const captured = new WeakSet<File>();
const blobs = new Map<string, Blob>();
let booted = false;
let lastProject = '';
const projectExtensions: Record<CraftId, string> = { photocraft: 'pcraft', vectorcraft: 'vectorcraft', filmcraft: 'fcproj', lightcraft: 'zip', pdfcraft: 'pdf', effectcraft: 'ecproj', designcraft: 'designcraft' };
const category: Record<CraftId, ToolCategory> = { photocraft: 'images', vectorcraft: 'vector', filmcraft: 'video', lightcraft: 'photos', pdfcraft: 'pdf', effectcraft: 'animation', designcraft: 'layout' };

function post(type: CraftMessage['type'], fields: Partial<CraftMessage> = {}) {
  if (parent !== window) parent.postMessage({ channel: 'editall-craft', appId, type, ...fields }, location.origin);
}
function describe(error: unknown) { return error instanceof Error ? error.message : String(error); }
function track<T>(promise: Promise<T>): Promise<T> {
  pending.add(promise);
  void promise.finally(() => pending.delete(promise)).catch(() => {});
  return promise;
}
async function flush() {
  await host.effectcraft?.flush?.();
  while (pending.size) await Promise.all([...pending]);
}
async function store(blob: Blob, name: string, role: FileRole) {
  const path = role === 'source' ? undefined : `/${role === 'project' ? 'projects' : 'exports'}/${name}`;
  const file = await track(putFile(blob, name, { appId, path, role }));
  if (role === 'project') lastProject = name;
  post('saved', { name, fileId: file.id });
  if (role === 'export') trackToolEvent('export_success', { category: category[appId], stage: 'export', format: name.split('.').pop()?.toLowerCase() });
  return file;
}
async function remember(files: File[]) {
  for (const file of files) {
    if (captured.has(file)) continue;
    captured.add(file);
    try { await store(file, file.name, 'source'); trackToolEvent('file_open', { category: category[appId], stage: 'open' }); }
    catch (error) { captured.delete(file); trackToolEvent('processing_error', { category: category[appId], stage: 'storage' }); throw error; }
  }
}
function fileRole(name: string): FileRole { return name.toLowerCase().endsWith(`.${projectExtensions[appId]}`) || /\.(psd|psb|idml)$/i.test(name) ? 'project' : 'export'; }
function interceptFiles() {
  const create = URL.createObjectURL.bind(URL);
  const revoke = URL.revokeObjectURL.bind(URL);
  URL.createObjectURL = (object: Blob | MediaSource) => { const url = create(object); if (object instanceof Blob) blobs.set(url, object); return url; };
  URL.revokeObjectURL = (url) => { blobs.delete(url); revoke(url); };
  const click = HTMLAnchorElement.prototype.click;
  HTMLAnchorElement.prototype.click = function () {
    const blob = blobs.get(this.href);
    if (blob && this.download) {
      void track(store(blob, this.download, fileRole(this.download))).catch((error) => post('error', { error: `Не удалось сохранить «${this.download}»: ${describe(error)}` }));
      return; // The library supplies an explicit download action for every saved file.
    }
    click.call(this);
  };
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.type === 'file' && input.files) void track(remember([...input.files])).catch((error) => post('error', { error: describe(error) }));
  }, true);
  window.addEventListener('drop', (event) => {
    if (event.dataTransfer?.files.length) void track(remember([...event.dataTransfer.files])).catch((error) => post('error', { error: describe(error) }));
  }, true);
}
async function indexedPreferences() {
  const cache = await getSettings(appId);
  const defaults: Partial<Record<CraftId, [string, Record<string, unknown>]>> = {
    photocraft: ['photocraft.preferences', { interface: { language: 'ru' } }],
    pdfcraft: ['pdfcraft', { language: 'ru' }],
    designcraft: ['designcraft.ui', { language: 'ru' }],
  };
  const seed = defaults[appId];
  if (seed && !(seed[0] in cache)) { cache[seed[0]] = JSON.stringify(seed[1]); await setSetting(appId, seed[0], cache[seed[0]]!); }
  const persist = (key: string, value: string | null) => {
    if (value === null) delete cache[key]; else cache[key] = value;
    void track(setSetting(appId, key, value)).catch((error) => post('error', { error: `Настройки не сохранены: ${describe(error)}` }));
  };
  const methods = {
    getItem: (key: string) => cache[String(key)] ?? null,
    setItem: (key: string, value: string) => persist(String(key), String(value)),
    removeItem: (key: string) => persist(String(key), null),
    key: (index: number) => Object.keys(cache)[index] ?? null,
    clear: () => { Object.keys(cache).forEach((key) => persist(key, null)); },
  };
  const storage = new Proxy(window.localStorage, {
    get(target, key) {
      if (key === 'length') return Object.keys(cache).length;
      if (typeof key === 'string' && key in methods) return methods[key as keyof typeof methods];
      if (typeof key === 'string' && key in cache) return cache[key];
      const value = Reflect.get(target, key, target); return typeof value === 'function' ? value.bind(target) : value;
    },
    set(_target, key, value) { if (typeof key === 'string') persist(key, String(value)); return true; },
    deleteProperty(_target, key) { if (typeof key === 'string') persist(key, null); return true; },
    ownKeys: () => Object.keys(cache),
    getOwnPropertyDescriptor(_target, key) { if (typeof key === 'string' && key in cache) return { enumerable: true, configurable: true, writable: true, value: cache[key] }; return undefined; },
  });
  Object.defineProperty(window, 'localStorage', { configurable: true, get: () => storage });
}
function shortcut(key: string, shift = false, command = true) {
  const canvas = document.querySelector('canvas');
  canvas?.focus();
  for (const type of ['keydown', 'keyup']) (canvas ?? document).dispatchEvent(new KeyboardEvent(type, { key, code: key.length === 1 ? `Key${key.toUpperCase()}` : key, ctrlKey: command, metaKey: command, shiftKey: shift, bubbles: true, cancelable: true }));
}
function dropFiles(files: File[]) {
  const canvas = document.querySelector('canvas');
  if (!canvas) throw new Error('Рабочая область не найдена');
  const transfer = new DataTransfer(); files.forEach((file) => transfer.items.add(file));
  const rect = canvas.getBoundingClientRect();
  for (const type of ['dragenter', 'dragover', 'drop']) canvas.dispatchEvent(new DragEvent(type, { dataTransfer: transfer, bubbles: true, cancelable: true, clientX: rect.x + rect.width / 2, clientY: rect.y + rect.height / 2 }));
}
async function importFiles(files: File[], alreadyStored = false) {
  if (!booted) throw new Error('Редактор ещё запускается');
  if (alreadyStored) files.forEach((file) => captured.add(file)); else await remember(files);
  if (appId === 'filmcraft' && host.filmcraft) {
    for (const file of files) {
      if (/\.fcproj$/i.test(file.name)) await host.filmcraft.openProject?.(file);
      else await host.filmcraft.importFiles?.([file]);
    }
  } else if (appId === 'effectcraft' && host.effectcraft) {
    for (const file of files) await host.effectcraft.addFile?.(file);
  } else if (appId === 'pdfcraft') {
    for (const file of files) window.dispatchEvent(new MessageEvent('message', { origin: location.origin, source: parent, data: { type: 'pdfcraft:open', name: file.name, bytes: await file.arrayBuffer() } }));
  } else {
    dropFiles(files);
  }
}
async function request(method: string, params: Record<string, unknown> = {}) {
  if (host.filmcraft) return host.filmcraft.request(method, params);
  if (host.effectcraft) return host.effectcraft.request(method, params);
  if (host.wasmBindings?.editallRequest) return JSON.parse(await host.wasmBindings.editallRequest(method, JSON.stringify(params))) as unknown;
  if (host.lightcraft && method === 'engine.commands') return JSON.parse(await host.lightcraft.command(method, '{}')) as unknown;
  if (host.lightcraft && method === 'engine.execute') return JSON.parse(await host.lightcraft.command(String(params.command), JSON.stringify(params.params ?? {}))) as unknown;
  throw new Error('У этого редактора нет браузерного командного API');
}
async function save() {
  if (!booted) throw new Error('Редактор ещё запускается');
  if (host.effectcraft?.saveToBrowser) {
    const result = await host.effectcraft.saveToBrowser(); await host.effectcraft.flush?.();
    const bytes = await host.effectcraft.readFile?.(result.path);
    if (!bytes) throw new Error('Редактор не вернул сохранённый проект');
    await store(new Blob([bytes as Uint8Array<ArrayBuffer>]), result.path.split('/').pop() ?? 'Проект.ecproj', 'project');
  } else if (host.filmcraft) {
    await host.filmcraft.execute('file.saveAs', { path: `/projects/${lastProject || 'Проект.fcproj'}` });
  } else if (host.lightcraft) {
    const result = JSON.parse(await host.lightcraft.command('file.backupLibrary', '{}')) as { ok?: boolean; error?: string };
    if (result.ok === false) throw new Error(result.error ?? 'Не удалось сохранить резервную копию');
  } else if (appId === 'designcraft' && host.wasmBindings?.editallRequest) {
    await request('engine.execute', { command: 'app.save', params: {} });
  } else if (appId === 'pdfcraft' && host.wasmBindings?.editallRequest) {
    await request('ui.command', { id: 'file.save' });
  } else shortcut('s');
  await flush();
}
async function setRussian() {
  if (host.filmcraft) await host.filmcraft.execute('app.language.russian', {});
  if (host.lightcraft) await host.lightcraft.command('app.language.russian', '{}');
  if (host.effectcraft) await host.effectcraft.execute('prefs.set', { key: 'general.language', value: 'ru' });
}
function watchNativeStart() {
  const loadingId: Partial<Record<CraftId, string>> = { photocraft: 'photocraft_loading', vectorcraft: 'vectorcraft_loading', designcraft: 'designcraft_loading', lightcraft: 'lightcraft_loading', filmcraft: 'loading', effectcraft: 'loading' };
  const began = performance.now();
  const timer = window.setInterval(async () => {
    const failure = host.filmcraftLoad?.error ?? host.effectcraftLoad?.error;
    if (failure) { clearInterval(timer); post('error', { error: `Не удалось запустить редактор: ${failure}` }); return; }
    const loading = loadingId[appId];
    const canvas = document.querySelector('canvas');
    const installed = !!(host.wasmBindings || host.filmcraft || host.effectcraft || host.lightcraft);
    const nativeReady = host.filmcraftLoad?.readyMs !== undefined || host.effectcraftLoad?.readyMs !== undefined;
    if (installed && canvas && canvas.width > 1 && canvas.height > 1 && (nativeReady || !loading || !document.getElementById(loading))) {
      clearInterval(timer);
      try { await setRussian(); booted = true; post('ready'); }
      catch (error) { post('error', { error: `Не удалось настроить язык: ${describe(error)}` }); }
    } else if (performance.now() - began > 120_000) {
      clearInterval(timer); post('error', { error: 'Редактор не запустился. Перезапустите его или выберите WebGL в меню.' });
    }
  }, 250);
}
async function boot() {
  if (!appId) throw new Error('Не указано приложение Craft');
  interceptFiles();
  await indexedPreferences();
  if (appId === 'filmcraft') installIndexedFileSystem(appId, (name, fileId) => post('saved', { name, fileId }));
  host.editallBridge = { appId, storage: 'indexeddb', importFiles, save, flush, request };
  window.addEventListener('message', (event: MessageEvent<CraftMessage>) => {
    if (event.source !== parent || event.origin !== location.origin || event.data?.channel !== 'editall-craft') return;
    const message = event.data;
    void (async () => {
      let result: unknown;
      if (message.type === 'import') {
        const files = (message.files ?? []).map((file) => new File([file.bytes], file.name, { type: file.type }));
        await importFiles(files, !!message.fileId);
        result = files.length;
      } else if (message.type === 'drop') {
        if (!booted) throw new Error('Редактор ещё запускается');
        const files = (message.files ?? []).map((file) => new File([file.bytes], file.name, { type: file.type }));
        await remember(files);
        dropFiles(files);
        result = files.length;
      } else if (message.type === 'save') await save();
      else if (message.type === 'rpc') result = await request(message.method ?? '', message.payload as Record<string, unknown>);
      else if (message.type === 'key') { const key = message.payload as { key: string; shift?: boolean; command?: boolean }; shortcut(key.key, key.shift, key.command ?? false); }
      else return;
      post('result', { id: message.id, result });
    })().catch((error) => post('result', { id: message.id, error: describe(error) }));
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchNativeStart, { once: true }); else watchNativeStart();
}
host.__editallCraftReady = boot();
void host.__editallCraftReady.catch((error) => post('error', { error: describe(error) }));
